/**
 * Which splat the proximity areas of splat swaps ask for, kept free of engine objects so it
 * is unit-testable. createViewer feeds it area enter / leave events (from a TriggerController)
 * and carries out the actions it returns.
 *
 * - The area entered last wins while several overlap.
 * - The first entry remembers the splat on screen; leaving every area goes back to it.
 * - An area entered with `keep` (hideOnExit: false) leaves its splat on screen when it is
 *   the last one left, but the areas are no longer active: chain swaps carry on from there.
 * - An area whose splat failed to load is ignored until the camera enters it afresh.
 * - A splat picked by hand (the scene menu) ends what the areas were doing: it stays until
 *   the camera enters an area afresh.
 * - Nothing is decided while a splat is loading; asking again after the load applies the
 *   latest state, so an enter or leave during a load is not lost.
 */
export type SwapAreaAction =
    | { kind: 'show'; url: string }
    /** Every area was left: go back to the splat from before the first one. */
    | { kind: 'return'; baseUrl: string }
    /** Every area was left and the last one keeps its splat (`url`, on screen). */
    | { kind: 'keep'; url: string };

export interface SwapAreaEnterOptions {
    /** The area keeps its splat on screen after the camera leaves (hideOnExit: false). */
    keep?: boolean;
}

export class SwapAreaState {
    private entered: string[] = [];
    private keepOnExit = new Set<string>();
    private failed = new Set<string>();
    private active = false;
    private base: string | null = null;
    private exitPending = false;
    /** The splat the areas last asked for: decides between keep and return on exit. */
    private lastWanted: string | null = null;

    /** The camera entered an area. A fresh entry also retries a splat that failed before. */
    enter(url: string, options: SwapAreaEnterOptions = {}): void {
        this.leave(url);
        this.entered.push(url);
        this.failed.delete(url);
        if (options.keep) this.keepOnExit.add(url);
        else this.keepOnExit.delete(url);
    }

    leave(url: string): void {
        const i = this.entered.indexOf(url);
        if (i >= 0) this.entered.splice(i, 1);
    }

    /** The splat of an area failed to load: stop asking for it until its area is entered afresh. */
    fail(url: string): void {
        if (this.entered.includes(url)) this.failed.add(url);
        if (this.lastWanted === url) this.lastWanted = null;
    }

    /**
     * A splat was picked by hand. It is what shows now and what stays after the camera leaves,
     * so the areas the camera is in are dropped: they take effect again on a fresh entry.
     */
    choose(): void {
        this.entered = [];
        this.failed.clear();
        this.active = false;
        this.base = null;
        this.exitPending = false;
        this.lastWanted = null;
    }

    /**
     * Start out active, holding `baseUrl` as the splat from before (a video capture taking over
     * from a visitor who stands in an area). The next call to next() then returns to it unless
     * an area is entered first.
     */
    resumeFrom(baseUrl: string): void {
        this.active = true;
        this.base = baseUrl;
        this.exitPending = false;
        this.lastWanted = null;
    }

    /** True from the first entry until the exit (return or keep) has been handed out. */
    get isActive(): boolean {
        return this.active;
    }

    /** The splat that was on screen when the first area was entered. */
    get baseUrl(): string | null {
        return this.base;
    }

    /**
     * The action to take now. `currentUrl` is the splat on screen; `loading` holds decisions
     * back until the swap in flight has finished.
     */
    next(currentUrl: string, loading: boolean): SwapAreaAction | null {
        let wanted: string | null = null;
        for (let i = this.entered.length - 1; i >= 0; i--) {
            if (!this.failed.has(this.entered[i])) {
                wanted = this.entered[i];
                break;
            }
        }
        if (wanted) {
            if (!this.active) {
                this.active = true;
                this.base = currentUrl;
            }
            this.exitPending = false;
            this.lastWanted = wanted;
            return wanted !== currentUrl && !loading ? { kind: 'show', url: wanted } : null;
        }
        if (this.active) {
            this.active = false;
            this.exitPending = true;
        }
        if (!this.exitPending || loading) return null;
        this.exitPending = false;
        const last = this.lastWanted;
        this.lastWanted = null;
        const baseUrl = this.base ?? currentUrl;
        this.base = null;
        // Keep only what the area actually put on screen; anything else goes back as usual.
        if (last && last === currentUrl && this.keepOnExit.has(last)) return { kind: 'keep', url: last };
        return { kind: 'return', baseUrl };
    }
}
